import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaClock,
  FaShieldAlt,
  FaHeart
} from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: #0a0f1c;
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing[12]} 0 ${({ theme }) => theme.spacing[6]};
  margin-top: auto;
  border-top: 1px solid rgba(255,255,255,0.1);
  text-align: center;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing[4]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[8]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[6]};
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${({ theme }) => theme.colors.action};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }

  p {
    color: ${({ theme }) => theme.colors.text.light};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
  }
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.light};
  text-decoration: none;
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.action};
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.text.light};

  svg {
    color: ${({ theme }) => theme.colors.action};
    flex-shrink: 0;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.action};
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.dark};
  margin-top: ${({ theme }) => theme.spacing[8]};
  padding-top: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.light};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  a {
    color: ${({ theme }) => theme.colors.action};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Logo = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  span {
    color: ${({ theme }) => theme.colors.action};
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
                            <Logo>
                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#5C9EAD' }}>
                      InflatableTx
                    </span>
                  </Logo>
          <p>
            Making your events unforgettable with premium bounce house rentals. 
            Safe, clean, and fun for all ages!
          </p>
          <SocialLinks>
            <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page">
              <FaFacebook aria-hidden="true" />
            </SocialLink>
            <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page">
              <FaInstagram aria-hidden="true" />
            </SocialLink>
            <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Visit our Twitter page">
              <FaTwitter aria-hidden="true" />
            </SocialLink>
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <h3>Quick Links</h3>
          <FooterLink to="/">Home</FooterLink>
          <FooterLink to="/products">Bounce Houses</FooterLink>
          <FooterLink to="/about">About Us</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
          <FooterLink to="/faq">FAQ</FooterLink>
        </FooterSection>

        <FooterSection>
          <h3>Contact Info</h3>
          <ContactInfo>
            <FaPhone />
            <span>(682) 202-5624</span>
          </ContactInfo>
          <ContactInfo>
            <FaEnvelope />
            <span>info@inflatabletx.com</span>
          </ContactInfo>
          <ContactInfo>
            <FaMapMarkerAlt />
            <span>100 E Vaughn, Cleburne, TX 76031</span>
          </ContactInfo>
          <ContactInfo>
            <FaClock />
            <span>Mon-Sat: 8AM-8PM, Sun: 9AM-6PM</span>
          </ContactInfo>
        </FooterSection>

        <FooterSection>
          <h3>Why Choose Us?</h3>
          <ContactInfo>
            <FaShieldAlt />
            <span>Fully Insured & Licensed</span>
          </ContactInfo>
          <ContactInfo>
            <FaShieldAlt />
            <span>Safety Certified Equipment</span>
          </ContactInfo>
          <ContactInfo>
            <FaShieldAlt />
            <span>Professional Setup & Cleanup</span>
          </ContactInfo>
          <ContactInfo>
            <FaShieldAlt />
            <span>24/7 Customer Support</span>
          </ContactInfo>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <p>
          © {currentYear} InflatableTx. All rights reserved. 
          Made with <FaHeart style={{ color: '#e74c3c', margin: '0 4px' }} /> 
          for your special events.
        </p>
        <p style={{ marginTop: '8px' }}>
          <a href="/privacy">Privacy Policy</a> • <a href="/terms">Terms of Service</a> • <a href="/safety">Safety Guidelines</a>
        </p>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;
